import { Component, OnDestroy, OnInit } from '@angular/core';
import { MaskApplierService } from 'ngx-mask';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { DynamicEvent } from 'src/app/main/dynamic/dynamic.event';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';
import { SharedDataService } from 'src/app/service/shared-data.service';

@Component({
  selector: 'app-input-text-default',
  templateUrl: './input-text-default.component.html',
  styleUrls: ['./input-text-default.component.scss']
})
export class InputTextDefaultComponent extends ComponentBase implements OnInit, OnDestroy {

  private initialValue: string

  constructor(
    private sharedDataService: SharedDataService,
    maskService: MaskApplierService) {
    super(maskService)
  }

  private get valueChanged(): boolean {
    return this.initialValue != this.attribute.value
  }

  get value(): string {
    const maskOpt = this.attribute.options
      .find(option => option.key.toLowerCase() == "mask");

    if(maskOpt) {
      const maskOptValue = maskOpt.value.toLowerCase();

      if(maskOptValue == "currency") {
        return this.currencyService.getCurrencyStringNoSeparator(this.attribute.value);
      } else if (maskOptValue == "date" || maskOptValue == "date br") {
        if (!this.attribute.value) {
          return "";
        }
        const valueStr = this.attribute.value;
        // Se salvo em ISO (yyyy-MM-dd), converter para dígitos ddMMyyyy para o input mascarado
        if (/^\d{4}-\d{2}-\d{2}/.test(valueStr)) {
          const [year, month, day] = valueStr.substring(0, 10).split('-');
          return `${day}${month}${year}`;
        }
        // Caso contrário, retornar apenas dígitos já existentes (parcial durante digitação)
        return valueStr.replace(/\D/g, '');
      }
    }

    if(this.attribute.value) {
      return this.attribute.value;
    } else {
      return "";
    }
  }

  set value(input: string) {
    const hasMaskOpt = this.attribute.options
      .some(option => option.key.toLowerCase() == "mask");

    if (hasMaskOpt && input) {
      const maskOpt = this.attribute.options
        .find(option => option.key.toLowerCase() == "mask");

      if (maskOpt && maskOpt.value.toLowerCase() == "currency") {
        const numericValue = input.replace(/\D/g, '');
        const formattedValue = (Number(numericValue) / 100).toFixed(2);
        this.attribute.value = formattedValue;
      } else if (maskOpt && (maskOpt.value.toLowerCase() == "date" || maskOpt.value.toLowerCase() == "date br")) {
        const digits = input.replace(/\D/g, '');
        if (digits.length >= 8) {
          const day = digits.substring(0, 2);
          const month = digits.substring(2, 4);
          const year = digits.substring(4, 8);
          // Salvar no formato ISO yyyy-MM-dd quando finalizado
          this.attribute.value = `${year}-${month}-${day}`;
        } else {
          // Enquanto incompleto, manter apenas dígitos
          this.attribute.value = digits;
        }
      } else {
        this.attribute.value = input.replace(/\D/g, '');
      }
    } else {
      this.attribute.value = input;
    }
  }

  ngOnInit() {
    this.initialValue = this.attribute.value
    this.shareData()
  }

  ngOnDestroy() {
    if(this.attribute.uid) {
      this.sharedDataService.remove(this.attribute.uid)
    }
  }

  onBlurInput() {
    this.executeChangingEvents();
  }

  private executeChangingEvents() {
    if (this.valueChanged) {
      let events: DynamicEvent[] = [];

      if (this.attribute.hasError && this.attribute.value) {
        this.attribute.hasError = false;
        events.push({ name: "validate" });
      }

      this.shareData(events);

      events.push({ name: "autosave", data: "silently" });
      this.emitEvents(events);
      this.initialValue = this.attribute.value;
    }
  }

  private shareData(events?: DynamicEvent[]) {
    let shareData = this.attribute.options
      .some(opt => opt.key.toLowerCase() == "sharedata" &&
        opt.value.toLowerCase() == "true");

    if (shareData && this.attribute.value) {
      if(!this.attribute.uid) {
        this.attribute.uid = performance.now().toString()
      }

      let sharingType = this.attribute.options
        .find(opt => opt.key.toLowerCase() == "sharingtype" && opt.value);

      if (sharingType && sharingType.value.toLowerCase() == "self") {
        let sharedDataKeyOpt = this.attribute.options
          .find(opt => opt.key.toLowerCase() == "SharedDataKey".toLowerCase() && opt.value);

        this.sharedDataService.setItem(
          sharedDataKeyOpt.value,
          { 
            uid: this.attribute.uid, 
            key: this.attribute.value, 
            value: this.attribute.value 
          },
          "items"
        );
      }

      if(events) {
        events.push({ name: "shareData", attribute: this.attribute })
      } else {
        this.emit({ name: "shareData", attribute: this.attribute })
      }
    }
  }

  getBooleanOptionByKey(attribute: AttributeModel, key: string): string {
    let optionValue = super.getOptionByKey(attribute, key)

    if(optionValue) {
      return optionValue
    }

    return "false"
  }

  getInputType(attribute: AttributeModel): string {
    if (attribute.type.toLowerCase() == "boolean")
      return "checkbox";
    else if (attribute.type.toLowerCase() == "booleanoexclusivo")
      return "radio";
    else if(attribute.type.toLowerCase() == "number")
      return "number"
    else
      return "text";
  }

  getLabel(): string {
    let label = this.attribute.options.find(o => o.key.toLowerCase() == "label")

    if(label) {
      return label.value
    }

    return this.attribute.key
  }

  getMask(isInputText = false): string {
    if (!this.attribute.options) {
      return "";
    }
    
    let opt = this.attribute.options
        .find(option => option.key.toLowerCase() == "mask");

    if (opt && opt.value) {
      const maskOptValue = opt.value.toLowerCase();

      // Currency: para input devemos sempre retornar uma máscara estável
      if(maskOptValue == "currency") {
        return this.getCurrencyMaskText(this.attribute, isInputText); 
      }

      if(maskOptValue == "date" || maskOptValue == "date br") {
        // Para input, sempre retornar o padrão de máscara
        if (isInputText) {
          return "00/00/0000";
        }
        // Para visualização, quando completo (>= 8 dígitos), formatar para BR usando ISO
        const raw = (this.attribute.value || "");
        const digits = raw.replace(/\D/g, '');
        if (digits.length >= 8 || /^\d{4}-\d{2}-\d{2}/.test(raw)) {
          const iso = /^\d{4}-\d{2}-\d{2}/.test(raw)
            ? raw.substring(0, 10)
            : `${digits.substring(4, 8)}-${digits.substring(2, 4)}-${digits.substring(0, 2)}`;
          const date = new Date(iso);
          return this.formatDateToBR(date);
        }
        return "";
      }

      // Para outras máscaras, se não há valor, não aplicar máscara
      if (!this.attribute.value) {
        return "";
      }

      if(maskOptValue == "document") {
        return this.getDocumentMask(this.attribute);
      } else if(maskOptValue == "phone") {
        return this.getPhoneMask(this.attribute, isInputText);
      } else if(maskOptValue == "percent") {
        return "000";
      } else {
        return opt.value;
      }
    }
    else {
      return "";
    }
  }

  getPrefix(): string {
    const maskOpt = this.attribute.options
      .find(option => option.key.toLowerCase() == "mask");

    if(maskOpt) {
      const maskOptValue = maskOpt.value.toLowerCase();

      if(maskOptValue == "currency") {
        return "R$ ";
      }
    }

    return "";
  }

  getValueForView() {
    const maskOpt = this.attribute.options
      .find(option => option.key.toLowerCase() == "mask");

    if(maskOpt) {
      const maskOptValue = maskOpt.value.toLowerCase();
      const maskValue = this.getMask();

      if(maskOptValue == "currency") {
        if (!this.attribute.value) {
          return "";
        }
        return this.currencyService.formatCurrencyValue(this.attribute.value);
      } else if (maskOptValue == "date" || maskOptValue == "date br") {
        if (maskValue) {
          return maskValue;
        }
        return "";
      } else if(maskOptValue == "percent") {
        return `${this.maskService.applyMask(this.attribute.value, maskValue)} %`;
      } else {
        return this.maskService.applyMask(this.attribute.value, maskValue);
      }
    }

    return this.attribute.value || "";
  }

  getPlaceholder(attribute: AttributeModel): string {
    return super.getOptionByKey(attribute, "placeholder", "")
  }

  onInputHandler(event: KeyboardEvent) {
    const inputElement = event.target as HTMLInputElement;

    const maskOpt = this.attribute.options
      .find(option => option.key.toLowerCase() == "mask");

    if (!maskOpt) {
      return;
    }

    if(maskOpt.value.toLowerCase() != "currency") {
      const maskValue = this.getMask();
  
      if (maskValue && inputElement.value.length >= maskValue.length) {
        this.executeChangingEvents();
      }
    } else if (maskOpt.value.toLowerCase() == "currency") {
      const inputText = inputElement.value;
      if (inputText.trim() === "") {
        setTimeout(() => {
          this.attribute.value = "";
          try {
            inputElement.value = "";
            inputElement.setSelectionRange(0, 0);
          } catch {}
        }, 0);
        return;
      }

      const digits = inputText.replace(/\D/g, '');
      if (digits.length === 0) {
        setTimeout(() => {
          this.attribute.value = "";
          try {
            inputElement.value = "";
            inputElement.setSelectionRange(0, 0);
          } catch {}
        }, 0);
        return;
      }
      const formattedValue = (Number(digits || '0') / 100).toFixed(2);
      this.attribute.value = formattedValue;

      setTimeout(() => {
        try {
          const end = (inputElement.value || '').length;
          inputElement.setSelectionRange(end, end);
        } catch {}
      }, 0);
    }
  }
}
